import {
  compact,
  isArray,
  isNil,
  isNumber,
  isString,
  isUndefined,
} from 'lodash';
import { isEnumValue, isObject } from './common';
import { type TypeGuardFn } from './utility-types';

export class TypeGuard {
  static isString = isString;
  static isNumber = isNumber;

  static merge<T, K extends T>(
    tGuard: TypeGuardFn<T>,
    kGuard: TypeGuardFn<K, T>
  ): TypeGuardFn<T & K> {
    return (data): data is T & K => tGuard(data) && kGuard(data);
  }

  static interface<T extends object>(
    typeGuardMap: Record<
      keyof T,
      TypeGuardFn<unknown> | TypeGuardFn<unknown>[]
    >,
    logError: boolean = false
  ): TypeGuardFn<T> {
    return (data): data is T =>
      isObject(data) &&
      Object.entries<TypeGuardFn<unknown> | TypeGuardFn<unknown>[]>(
        typeGuardMap
      ).every(([key, guardFns]) => {
        const value = data[key];
        const passed = guardFnPasses(guardFns, value);
        if (!passed && logError) {
          // eslint-disable-next-line no-console
          console.error(
            `TypeGuard.interface failed for key: ${key}, value: ${String(
              value
            )}`,
            value
          );
        }
        return passed;
      });
  }

  /**
   * Rejects data carrying keys the guard map does not declare, unlike
   * `interface`, which ignores them. Use at trust boundaries so a caller cannot
   * smuggle extra fields through into a persisted document.
   */
  static exactInterface<T extends object>(
    typeGuardMap: Record<
      keyof T,
      TypeGuardFn<unknown> | TypeGuardFn<unknown>[]
    >,
    logError: boolean = false
  ): TypeGuardFn<T> {
    const declaredKeys = new Set(Object.keys(typeGuardMap));
    const guardFn = TypeGuard.interface<T>(typeGuardMap, logError);
    return (data): data is T =>
      guardFn(data) &&
      Object.keys(data as object).every((key) => declaredKeys.has(key));
  }

  static nonEmptyString(data: unknown): data is string {
    return isString(data) && data.length > 0;
  }

  static positiveInteger(data: unknown): data is number {
    return isNumber(data) && Number.isInteger(data) && data > 0;
  }

  static arrayOf<T>(guardFn: TypeGuardFn<T>): TypeGuardFn<T[]> {
    return (data): data is T[] =>
      isArray(data) && data.every((item) => guardFn(item));
  }

  static nilOr<T>(
    guardFn: TypeGuardFn<unknown> | TypeGuardFn<unknown>[]
  ): TypeGuardFn<T | undefined | null> {
    return (data): data is T | undefined | null =>
      isNil(data) ? true : guardFnPasses(guardFn, data);
  }

  static undefinedOr<T>(guardFn: TypeGuardFn<T>): TypeGuardFn<T | undefined> {
    return (data): data is T | undefined =>
      isUndefined(data) ? true : guardFn(data);
  }

  static enumValue<T>(enumType: T): (data: unknown) => data is T[keyof T] {
    return (data): data is T[keyof T] => isEnumValue(enumType, data);
  }

  static isOneOf<P, Q, R>(
    guardFn1: TypeGuardFn<P>,
    guardFn2: TypeGuardFn<Q>,
    guardFn3?: TypeGuardFn<R>
  ): TypeGuardFn<P | Q> {
    return (data): data is P | Q =>
      compact([guardFn1, guardFn2, guardFn3]).some((guardFn) => guardFn(data));
  }

  static noGuard<T>(): TypeGuardFn<T> {
    return (data): data is T => (data ? true : true);
  }
}

/**
 * @deprecated Use TypeGuard.arrayOf instead
 */
export function isArrayOfGuard<T>(
  data: unknown,
  guardFn: TypeGuardFn<T>
): data is T[] {
  return isArray(data) && data.every((item) => guardFn(item));
}

function guardFnPasses(
  guardFns: TypeGuardFn<unknown> | TypeGuardFn<unknown>[],
  value: unknown
): boolean {
  if (!isArray(guardFns)) {
    return guardFns(value);
  }

  return guardFns.some((guardFn) => guardFn(value));
}
